/**
 * AI Chat Drawer
 * Main chat interface drawer component
 */

import { useState, useEffect, useRef } from "react";
import { MdAutoAwesome, MdSend, MdRefresh } from "react-icons/md";
import { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import Button, { IconButton } from "components/button";
import SideDrawer from "components/drawer";
import { Textarea } from "components/fields";
import { EmptyState } from "components/states";
import notify from "lib/notify";
import aiChatService from "services/aiChatService";
import documentsService from "services/documentsService";
import AIChatMessages from "./AIChatMessages";

const AIChatDrawer = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isAvailable, setIsAvailable] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
      checkAvailability();
    }
  }, [isOpen]);

  // Keep the latest message in view
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  const loadSuggestions = async () => {
    const data = await aiChatService.getSuggestions();
    setSuggestions(data);
  };

  const checkAvailability = async () => {
    const status = await aiChatService.getStatus();
    setIsAvailable(status.available);
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isStreaming) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setIsStreaming(true);
    setStreamingContent("");

    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);

    // Conversation history for the API (excluding the message being sent)
    const apiMessages = newMessages.slice(0, -1).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    let fullContent = "";

    abortControllerRef.current = await aiChatService.sendMessage(apiMessages, userMessage, {
      onChunk: (chunk) => {
        fullContent += chunk;
        setStreamingContent(fullContent);
      },
      onComplete: (artifacts) => {
        setMessages((prev) => [...prev, { role: "assistant", content: fullContent, artifacts }]);
        setStreamingContent("");
        setIsStreaming(false);
      },
      onError: (error) => {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: error || "Something went wrong. Please try again later.",
            isError: true,
          },
        ]);
        setStreamingContent("");
        setIsStreaming(false);
        notify.error("Could not get a response from Atrium AI");
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setStreamingContent("");
    setIsStreaming(false);
    loadSuggestions();
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const handleSaveArtifact = async (artifact) => {
    try {
      await documentsService.createDocument(artifact);
      notify.success("Saved to Documents");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save document"));
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  const canSend = !isStreaming && Boolean(inputValue.trim()) && isAvailable;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      initialFocusRef={inputRef}
      title={
        <span className="flex items-center gap-2">
          <MdAutoAwesome aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
          Atrium AI
        </span>
      }
      subtitle="Ask about your church's data"
      headerActions={
        messages.length > 0 && (
          <Button variant="ghost" size="sm" leftIcon={<MdRefresh />} onClick={handleNewChat}>
            New Chat
          </Button>
        )
      }
      footer={
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2">
          <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              aria-label="Message Atrium AI"
              className="min-w-0 flex-1"
              rows={2}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your organization…"
              disabled={isStreaming || !isAvailable}
            />
            <IconButton
              type="submit"
              variant="primary"
              icon={<MdSend />}
              label="Send message"
              disabled={!canSend}
            />
          </div>
          <p className="text-center text-xs text-fg-muted">Session only. Chat history is not saved.</p>
        </form>
      }
    >
      {!isAvailable && (
        <AlertPane tone="warning">
          AI service is not configured. Please contact your administrator.
        </AlertPane>
      )}

      {messages.length === 0 ? (
        <div className="flex flex-col items-center">
          <EmptyState
            icon={MdAutoAwesome}
            title="How Can I Help You Today?"
            description="Ask me anything about your organization's data."
            className="pb-6"
          />
          {suggestions.length > 0 && (
            <ul className="w-full space-y-2" aria-label="Suggested questions">
              {suggestions.map((suggestion, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="focus-ring block w-full rounded-control border border-line bg-surface px-3 py-2.5 text-left text-sm text-fg transition-colors hover:bg-surface-subtle"
                  >
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <AIChatMessages
          messages={messages}
          streamingContent={streamingContent}
          isStreaming={isStreaming}
          onSaveArtifact={handleSaveArtifact}
        />
      )}
      <div ref={messagesEndRef} />
    </SideDrawer>
  );
};

export default AIChatDrawer;
